import { useCallback, useEffect, useRef, useState } from 'react'
import { Descendant } from 'slate'

import { formatDescendantToString, parseStringToDescendant } from '@/lib/slate'
import { debounce } from '@/shared/utils'

export const useVariableTextEdit = ({
  value = '',
  onChange,
}: {
  value?: string
  onChange(value: string, localValue: Descendant[]): void
}) => {
  const cleanedValue = typeof value === 'string' ? value : JSON.stringify(value)

  const [localValue, setLocalValue] = useState<Descendant[]>(() => parseStringToDescendant(cleanedValue))

  const skipUpdate = useRef(false)

  const debouncedUpdate = useRef(
    debounce((newValue: Descendant[]) => {
      skipUpdate.current = true
      onChange(formatDescendantToString(newValue), newValue)
    }, 800)
  ).current

  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false
      return
    }

    const newValue = parseStringToDescendant(cleanedValue)
    setLocalValue(newValue)
  }, [cleanedValue])

  const handleChange = useCallback((newValue: Descendant[]) => {
    setLocalValue(newValue)
    debouncedUpdate(newValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel()
    }
  }, [debouncedUpdate])

  return [localValue, handleChange] as const
}
